import type { WebGPURenderer } from 'three/webgpu';
import { flags } from './flags';
import { Input } from './Input';

/**
 * A system participates in the frame loop. Simulation runs at a fixed rate
 * (fixedUpdate); presentation runs once per rendered frame (frameUpdate) with
 * `alpha` = how far we are between the last two simulation states, for
 * interpolation.
 */
export interface System {
  readonly name: string;
  fixedUpdate?(dt: number): void;
  frameUpdate?(dt: number, alpha: number): void;
}

const MAX_STEPS_PER_FRAME = 5;

export class Engine {
  readonly fixedDt = 1 / 60;
  readonly input: Input;
  simTime = 0;
  frameCount = 0;
  paused = false;
  timeScale = 1;
  /** Seconds between the last two rendered frames (clamped). */
  frameDt = 1 / 60;
  /** Renders the frame; set by the game. */
  render: (() => void) | null = null;

  private readonly systems: System[] = [];
  private accumulator = 0;
  private lastTime = -1;

  constructor(
    readonly renderer: WebGPURenderer,
    readonly canvas: HTMLCanvasElement,
  ) {
    this.input = new Input(canvas);
  }

  add<T extends System>(system: T): T {
    this.systems.push(system);
    return system;
  }

  remove(system: System): void {
    const i = this.systems.indexOf(system);
    if (i >= 0) this.systems.splice(i, 1);
  }

  start(): void {
    if (flags.str('tick', 'raf') === 'timer') {
      // Test mode: drive frames from a timer so automation works in hidden/background views
      // where requestAnimationFrame is paused.
      setInterval(() => {
        this.renderer.info.reset();
        this.tick(performance.now());
      }, 16);
      return;
    }
    this.renderer.setAnimationLoop((t: number) => this.tick(t));
  }

  /**
   * Test helper: run the simulation forward synchronously (no rendering), independent of
   * browser timer throttling. perStep runs before every fixed step.
   */
  advance(seconds: number, perStep?: () => void): void {
    const steps = Math.round(seconds / this.fixedDt);
    for (let i = 0; i < steps; i++) {
      perStep?.();
      for (const s of this.systems) s.fixedUpdate?.(this.fixedDt);
      this.input.consumeEdges();
      this.simTime += this.fixedDt;
    }
  }

  private tick(nowMs: number): void {
    const now = nowMs / 1000;
    const dt = this.lastTime < 0 ? this.fixedDt : Math.min(now - this.lastTime, 0.25);
    this.lastTime = now;
    this.frameDt = dt;
    this.input.pollGamepads(dt);

    if (!this.paused) {
      this.accumulator += dt * this.timeScale;
      let steps = 0;
      while (this.accumulator >= this.fixedDt && steps < MAX_STEPS_PER_FRAME) {
        for (const s of this.systems) s.fixedUpdate?.(this.fixedDt);
        this.input.consumeEdges();
        this.simTime += this.fixedDt;
        this.accumulator -= this.fixedDt;
        steps++;
      }
      // Drop time instead of spiralling when the machine can't keep up.
      if (steps === MAX_STEPS_PER_FRAME) this.accumulator = 0;
    }

    const alpha = this.accumulator / this.fixedDt;
    for (const s of this.systems) s.frameUpdate?.(dt, alpha);
    this.render?.();
    this.frameCount++;
  }
}
